import useReduxState from '@portal/hooks/useReduxState';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdvancedProfile from '@portal/components/AdvancedProfile/AdvancedProfile';
import NotificationWidget from '@portal/components/NotificationWidget';
import ConfigIcon from '@portal/assets/svg/my-account/ic_config.svg?react';
import AdvancedBreadcrumbs from '@portal/components/AdvancedBreadcrumbs/AdvancedBreadcrumbs';
import { translate } from '@portal/services/i18n';
import { ThemeType, THEME_CONFIG, THEME_NAMES } from '@portal/enum/ThemeType';
import { SET_THEME } from '@portal/actions/actionTypes';
import { UserImpersonationActions } from '@portal/actions';
import { useDispatch } from 'react-redux';
import User from '@portal/assets/svg/profile/user.svg?react';
import ThemedText from '@portal/components/ThemedText/ThemedText';

type AdvancedHeaderProps = {
  title?: string;
  showBreadCrumbs?: boolean;
  breadCrumbs?: Array<{
    active: boolean;
    title: string;
    url?: string;
    state?: unknown;
    icon?: string;
  }>;
};

const AdvancedHeader = (props: AdvancedHeaderProps) => {
  const dispatch = useDispatch();
  const {
    title = translate('APPLICATION.TITLE'),
    showBreadCrumbs,
    breadCrumbs,
  } = props;
  const {
    user: { me },
    theme: { currentTheme },
  } = useReduxState();

  const [openProfile, setOpenProfile] = useState(false);
  const [openThemeSelector, setOpenThemeSelector] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<ThemeType>(
    ThemeType.DEFAULT
  );

  const profileRef = useRef(null);
  const themeSelectorRef = useRef(null);

  useEffect(() => {
    const theme = localStorage.getItem('selected-theme');
    setSelectedTheme(
      theme && Object.values(ThemeType).includes(theme as ThemeType)
        ? (theme as ThemeType)
        : ThemeType.DEFAULT
    );
  }, [currentTheme]);

  const handleMouseClick = useCallback((event) => {
    const dropdownElement = document.querySelector(
      '.advanced-header__dropdown'
    );
    const dropdownRow = document.querySelector('.advanced-header__row');
    const themeSelectorElement = document.querySelector(
      '.advanced-header__theme-dropdown'
    );

    if (
      dropdownElement &&
      dropdownRow &&
      !dropdownElement.contains(event.target) &&
      !dropdownRow.contains(event.target)
    ) {
      setOpenProfile(false);
    }

    if (
      themeSelectorElement &&
      !themeSelectorElement.contains(event.target) &&
      !event.target.closest('.advanced-header__theme-selector')
    ) {
      setOpenThemeSelector(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleMouseClick);

    return () => {
      document.removeEventListener('click', handleMouseClick);
    };
  }, [handleMouseClick]);

  const toggleDropdown = () => {
    setOpenProfile((prevOpenProfile) => !prevOpenProfile);
  };

  const toggleThemeSelector = () => {
    setOpenThemeSelector((prevOpen) => !prevOpen);
  };

  const handleThemeChange = (theme: ThemeType) => {
    localStorage.setItem('selected-theme', theme);
    dispatch({ type: SET_THEME, payload: theme });
    setSelectedTheme(theme);
    setOpenThemeSelector(false);
  };

  useEffect(() => {
    dispatch(UserImpersonationActions.getUserImpersonationLastAccess());
  }, [dispatch]);

  return (
    <div className="advanced-header">
      <div>
        <ThemedText
          variant="h1"
          className="advanced-header__title"
          style={{ color: currentTheme.primaryColor }}
        >
          {title}
        </ThemedText>
        {showBreadCrumbs ? (
          <div className="advanced-header__breadCrumbs">
            <AdvancedBreadcrumbs items={breadCrumbs ?? []} />
          </div>
        ) : null}
      </div>
      <div className="advanced-header__actions">
        <div className="advanced-header__theme-selector" ref={themeSelectorRef}>
          <button
            className="advanced-header__theme-button"
            onClick={toggleThemeSelector}
            title={translate('PAGES.MY_ACCOUNT.TABS.THEME_SELECTION')}
            aria-label={translate('PAGES.MY_ACCOUNT.TABS.THEME_SELECTION')}
          >
            <ConfigIcon />
          </button>
          {openThemeSelector ? (
            <div className="advanced-header__theme-dropdown">
              <div className="advanced-header__theme-header">
                <ThemedText
                  variant="h4"
                  className="advanced-header__theme-title"
                >
                  {translate('PAGES.MY_ACCOUNT.TABS.THEME_SELECTION')}
                </ThemedText>
              </div>
              <div className="advanced-header__theme-options">
                {Object.values(ThemeType).map((theme) => (
                  <div
                    key={theme}
                    className={`advanced-header__theme-option ${
                      selectedTheme === theme
                        ? 'advanced-header__theme-option--selected'
                        : ''
                    }`}
                    onClick={() => handleThemeChange(theme)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleThemeChange(theme);
                      }
                    }}
                  >
                    <div
                      className="advanced-header__theme-preview"
                      style={{
                        background: THEME_CONFIG[theme].background,
                        border: `2px solid ${THEME_CONFIG[theme].borderColor}`,
                      }}
                    />
                    <div className="advanced-header__theme-label">
                      {THEME_NAMES[theme]}
                    </div>
                    {selectedTheme === theme && (
                      <div className="advanced-header__theme-check">✓</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <NotificationWidget />
      </div>
      <div
        className="advanced-header__user-profile"
        onClick={(e) => {
          e.stopPropagation();
          toggleDropdown();
        }}
        ref={profileRef}
      >
        <div
          className="advanced-header__avatar"
          style={{ border: `1px solid ${currentTheme.borderColor}` }}
        >
          {!imageError &&
          (me?.impersonatorAccount
            ? me?.impersonatorAccount?.photoUrl
            : me.account.photoUrl) ? (
            <img
              src={
                me?.impersonatorAccount
                  ? me?.impersonatorAccount?.photoUrl
                  : me.account.photoUrl
              }
              className="advanced-header__avatar_img"
              alt="advanced-header_image"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="advanced-header__avatar_fallback">
              <User />
            </div>
          )}
        </div>
        <div className="advanced-header__info">
          <ThemedText variant="h4" className="advanced-header__info__user">
            {me.name}
          </ThemedText>
          <div className="advanced-header__row">
            <ThemedText variant="body">{me.email}</ThemedText>
          </div>
        </div>
        {openProfile ? (
          <div className="advanced-header__dropdown">
            <AdvancedProfile
              name={me.name ?? ''}
              profile={me.impersonatorAccount?.name ?? me.account.name ?? ''}
              mail={me.email ?? ''}
              login={me.login ?? ''}
              photoUrl={
                me?.impersonatorAccount
                  ? me?.impersonatorAccount?.photoUrl ?? ''
                  : me.account.photoUrl ?? ''
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdvancedHeader;
